import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { useProject } from '@/hooks/use-projects';
import { useCreateTask, useProjectTags } from '@/hooks/use-tasks';
import { useEscapeKey } from '@/hooks/use-escape-key';
import { cn } from '@/lib/utils';
import type { TaskStatusItem, TaskPriority, TaskType } from '@hygge/shared';
import {
  Check,
  Loader2,
  Plus,
  Tag,
  Timer,
  X,
} from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { DescriptionEditor } from './description-editor';
import {
  TYPE_CONFIG,
  PRIORITY_CONFIG,
  MODAL_WIDTH,
  PriorityBars,
  ChipButton,
  ChipSeparator,
  AssigneeChip,
  EstimateChip,
  DueDateChip,
} from './task-config';

// ─── Types & Config ───────────────────────────────────────────────────────

interface CreateTaskModalProps {
  projectId: string;
  statuses: TaskStatusItem[];
  defaultStatusId?: string;
  defaultSprintId?: string;
  sprintName?: string;
  onClose: () => void;
}

// ─── Main Modal ───────────────────────────────────────────────────────────

export function CreateTaskModal({ projectId, statuses, defaultStatusId, defaultSprintId, sprintName, onClose }: CreateTaskModalProps) {
  const defaultStatus = defaultStatusId
    ? statuses.find((s) => s.id === defaultStatusId)
    : statuses.find((s) => s.category === 'backlog') ?? statuses[0];

  const [type, setType] = useState<TaskType>('task');
  const [title, setTitle] = useState('');
  const [statusId, setStatusId] = useState(defaultStatus?.id ?? '');
  const [priority, setPriority] = useState<TaskPriority>('none');
  const [assigneeIds, setAssigneeIds] = useState<string[]>([]);
  const [estimate, setEstimate] = useState<number | null>(null);
  const [tagIds, setTagIds] = useState<string[]>([]);
  const [dueDate, setDueDate] = useState<string | null>(null);
  const [description, setDescription] = useState('');
  const [openChip, setOpenChip] = useState<string | null>(null);

  const titleRef = useRef<HTMLInputElement>(null);

  const createMutation = useCreateTask(projectId);
  const { data: projectDetail } = useProject(projectId);
  const { data: projectTags } = useProjectTags(projectId);

  const typeInfo = TYPE_CONFIG[type];
  const TypeIcon = typeInfo.icon;
  const nextKey = projectDetail ? `${projectDetail.slug}-${projectDetail.taskCounter + 1}` : '...';

  const handleSubmit = useCallback(async () => {
    if (!title.trim() || createMutation.isPending) return;
    await createMutation.mutateAsync({
      title: title.trim(),
      statusId,
      priority,
      type,
      assigneeIds: assigneeIds.length > 0 ? assigneeIds : undefined,
      storyPoints: estimate ?? undefined,
      tagIds: tagIds.length > 0 ? tagIds : undefined,
      dueDate: dueDate || undefined,
      description: description.trim() || undefined,
      sprintId: defaultSprintId || undefined,
    });
    onClose();
  }, [title, statusId, priority, type, assigneeIds, estimate, tagIds, dueDate, description, createMutation, onClose]);

  const isPending = createMutation.isPending;

  useEffect(() => {
    const id = requestAnimationFrame(() => titleRef.current?.focus());
    return () => cancelAnimationFrame(id);
  }, []);

  // Escape closes the modal — but stands down when a nearer handler (Radix popover,
  // inline edit) already consumed it.
  useEscapeKey(() => onClose(), { target: 'window' });

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
        e.preventDefault();
        handleSubmit();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleSubmit]);

  const toggleChip = (name: string) => {
    setOpenChip((prev) => (prev === name ? null : name));
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm animate-in fade-in-0 duration-150"
      role="dialog"
      aria-modal="true"
      aria-label={`New ${typeInfo.label.toLowerCase()}`}
    >
      <div className={cn('w-full rounded-xl border border-border bg-card shadow-2xl animate-in zoom-in-95 fade-in-0 duration-150 max-h-[85vh] flex flex-col', MODAL_WIDTH)}>
        {/* ── Section 1: Header ── */}
        <div className="flex items-center gap-2.5 px-5 pt-4 pb-2 shrink-0">
          {/* Type icon button */}
          <Popover open={openChip === 'header-type'} onOpenChange={(o) => o ? toggleChip('header-type') : setOpenChip(null)}>
            <PopoverTrigger asChild>
              <button
                type="button"
                className={cn(
                  'flex items-center justify-center h-7 w-7 rounded-md border cursor-pointer transition-colors',
                  typeInfo.bg, typeInfo.border,
                )}
              >
                <TypeIcon className={cn('h-3.5 w-3.5', typeInfo.color)} />
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-36 p-1" align="start">
              {(Object.entries(TYPE_CONFIG) as [TaskType, typeof typeInfo][]).map(([val, cfg]) => {
                const Icon = cfg.icon;
                return (
                  <button
                    key={val}
                    type="button"
                    className={cn(
                      'flex items-center gap-2 w-full rounded-md px-2.5 py-1.5 text-xs hover:bg-accent cursor-pointer transition-colors',
                      type === val && 'bg-accent',
                    )}
                    onClick={() => { setType(val); setOpenChip(null); }}
                  >
                    <Icon className={cn('h-3.5 w-3.5', cfg.color)} />
                    {cfg.label}
                  </button>
                );
              })}
            </PopoverContent>
          </Popover>

          <span
            className={cn(
              'inline-flex items-center rounded-md px-2 py-0.5 text-xs font-mono font-semibold text-white shadow-sm',
              !projectDetail?.color && 'bg-primary/90',
            )}
            style={
              projectDetail?.color
                ? { backgroundColor: projectDetail.color }
                : undefined
            }
          >
            {nextKey}
          </span>
          <span className="text-sm text-muted-foreground">
            New {typeInfo.label.toLowerCase()}
          </span>

          <div className="flex-1" />
          <button
            type="button"
            className="flex items-center justify-center h-7 w-7 rounded-full text-muted-foreground hover:text-foreground hover:bg-accent transition-colors cursor-pointer"
            onClick={onClose}
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* ── Section 2: Title ── */}
        <div className="px-5 py-2 shrink-0">
          <input
            ref={titleRef}
            data-slot="task-title-input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={`${typeInfo.label} title`}
            // A bordered field like every other, so the title reads as something
            // to type into; it used to be a borderless heading. [HW-926]
            className="w-full rounded-md border border-input bg-transparent px-3 py-1.5 text-lg font-semibold text-foreground placeholder:text-muted-foreground/60 outline-none focus-visible:ring-2 focus-visible:ring-ring"
          />
        </div>

        {/* ── Section 3: Meta Chips ── */}
        <div className="px-5 py-2.5 flex flex-wrap items-center gap-1.5 shrink-0">
          {/* Type chip */}
          <Popover open={openChip === 'type'} onOpenChange={(o) => o ? toggleChip('type') : setOpenChip(null)}>
            <PopoverTrigger asChild>
              <ChipButton className={cn(typeInfo.bg, typeInfo.border)}>
                <TypeIcon className={cn('h-3 w-3', typeInfo.color)} />
                <span className={typeInfo.color}>{typeInfo.label}</span>
              </ChipButton>
            </PopoverTrigger>
            <PopoverContent className="w-36 p-1" align="start">
              {(Object.entries(TYPE_CONFIG) as [TaskType, typeof typeInfo][]).map(([val, cfg]) => {
                const Icon = cfg.icon;
                return (
                  <button
                    key={val}
                    type="button"
                    className={cn(
                      'flex items-center gap-2 w-full rounded-md px-2.5 py-1.5 text-xs hover:bg-accent cursor-pointer transition-colors',
                      type === val && 'bg-accent',
                    )}
                    onClick={() => { setType(val); setOpenChip(null); }}
                  >
                    <Icon className={cn('h-3.5 w-3.5', cfg.color)} />
                    {cfg.label}
                  </button>
                );
              })}
            </PopoverContent>
          </Popover>

          <ChipSeparator />

          {/* Status chip */}
          <Popover open={openChip === 'status'} onOpenChange={(o) => o ? toggleChip('status') : setOpenChip(null)}>
            <PopoverTrigger asChild>
              <ChipButton>
                <div
                  className="h-2 w-2 rounded-full"
                  style={{ backgroundColor: statuses.find((s) => s.id === statusId)?.color }}
                />
                <span>{statuses.find((s) => s.id === statusId)?.name ?? 'Status'}</span>
              </ChipButton>
            </PopoverTrigger>
            <PopoverContent className="w-44 p-1" align="start">
              {statuses.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  className={cn(
                    'flex items-center gap-2 w-full rounded-md px-2.5 py-1.5 text-xs hover:bg-accent cursor-pointer transition-colors',
                    statusId === s.id && 'bg-accent',
                  )}
                  onClick={() => { setStatusId(s.id); setOpenChip(null); }}
                >
                  <div className="h-2 w-2 rounded-full" style={{ backgroundColor: s.color }} />
                  {s.name}
                </button>
              ))}
            </PopoverContent>
          </Popover>

          {/* Priority chip */}
          <Popover open={openChip === 'priority'} onOpenChange={(o) => o ? toggleChip('priority') : setOpenChip(null)}>
            <PopoverTrigger asChild>
              <ChipButton>
                <PriorityBars bars={PRIORITY_CONFIG[priority].bars} className={PRIORITY_CONFIG[priority].color} />
                <span>{PRIORITY_CONFIG[priority].label}</span>
              </ChipButton>
            </PopoverTrigger>
            <PopoverContent className="w-40 p-1" align="start">
              {(Object.entries(PRIORITY_CONFIG) as [TaskPriority, typeof PRIORITY_CONFIG.none][]).map(([val, cfg]) => (
                <button
                  key={val}
                  type="button"
                  className={cn(
                    'flex items-center gap-2 w-full rounded-md px-2.5 py-1.5 text-xs hover:bg-accent cursor-pointer transition-colors',
                    priority === val && 'bg-accent',
                  )}
                  onClick={() => { setPriority(val); setOpenChip(null); }}
                >
                  <PriorityBars bars={cfg.bars} className={cfg.color} />
                  {cfg.label}
                </button>
              ))}
            </PopoverContent>
          </Popover>

          {/* Assignee chip */}
          <AssigneeChip
            projectId={projectId}
            value={assigneeIds}
            open={openChip === 'assignee'}
            onToggle={() => toggleChip('assignee')}
            onClose={() => setOpenChip(null)}
            onChange={setAssigneeIds}
          />

          {/* Estimate chip */}
          <EstimateChip
            projectId={projectId}
            value={estimate}
            open={openChip === 'estimate'}
            onToggle={() => toggleChip('estimate')}
            onClose={() => setOpenChip(null)}
            onChange={setEstimate}
          />

          {/* Due date chip */}
          <DueDateChip
            value={dueDate}
            open={openChip === 'dueDate'}
            onToggle={() => toggleChip('dueDate')}
            onClose={() => setOpenChip(null)}
            onChange={setDueDate}
          />

          <ChipSeparator />

          {/* Tags chip */}
          <Popover open={openChip === 'tags'} onOpenChange={(o) => o ? toggleChip('tags') : setOpenChip(null)}>
            <PopoverTrigger asChild>
              <ChipButton dashed={tagIds.length === 0}>
                {tagIds.length > 0 ? (
                  <span className="flex items-center gap-1">
                    {projectTags
                      ?.filter((t) => tagIds.includes(t.id))
                      .map((t) => (
                        <span
                          key={t.id}
                          className="inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-medium"
                          style={{ backgroundColor: `${t.color}20`, color: t.color }}
                        >
                          <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: t.color }} />
                          {t.name}
                        </span>
                      ))}
                  </span>
                ) : (
                  <>
                    <Tag className="h-3 w-3" />
                    <span>+ Tag</span>
                  </>
                )}
              </ChipButton>
            </PopoverTrigger>
            <PopoverContent className="w-52 p-1" align="start">
              {projectTags?.map((t) => {
                const active = tagIds.includes(t.id);
                return (
                  <button
                    key={t.id}
                    type="button"
                    className={cn(
                      'flex items-center gap-2 w-full rounded-md px-2.5 py-1.5 text-xs hover:bg-accent cursor-pointer transition-colors',
                      active && 'bg-accent',
                    )}
                    onClick={() =>
                      setTagIds((prev) =>
                        active ? prev.filter((x) => x !== t.id) : [...prev, t.id],
                      )
                    }
                  >
                    <Check className={cn('h-3 w-3', active ? 'opacity-100' : 'opacity-0')} />
                    <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ backgroundColor: t.color }} />
                    {t.name}
                  </button>
                );
              })}
              {(!projectTags || projectTags.length === 0) && (
                <p className="px-2.5 py-2 text-xs text-muted-foreground">No tags yet. Create tags in project settings.</p>
              )}
            </PopoverContent>
          </Popover>
        </div>

        {/* ── Section 4: Description Editor ── */}
        <DescriptionEditor
          value={description}
          onChange={setDescription}
          placeholder={`Describe the ${typeInfo.label.toLowerCase()} — what needs to be done, acceptance criteria, context...\n\nTip: click \u2726 AI in the toolbar to generate from a prompt.`}
          className="mx-5 mb-3 shrink-0"
        />

        {/* ── Section 5: Footer ── */}
        <div className="flex items-center justify-between px-5 py-3 border-t border-border shrink-0">
          <div className="flex items-center gap-3">
            <span className="text-xs text-muted-foreground font-mono">
              ⌘ Enter to create
            </span>
            {defaultSprintId && sprintName && (
              <span className="inline-flex items-center gap-1 text-xs text-green-500 bg-green-500/10 rounded-md px-2 py-0.5">
                <Timer className="h-3 w-3" />
                {sprintName}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              disabled={!title.trim() || isPending}
              onClick={handleSubmit}
              className="gap-1.5"
            >
              {isPending ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Plus className="h-3.5 w-3.5" />
              )}
              Create {typeInfo.label}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

